// Mirrors findingsSyncArgsSchema in packages/middleware/src/findings/
// findings-sync.runner.ts — keep the shape in sync: { patient?, since? } where since is
// absent (unprocessed reports), a YYYY-MM-DD date (UTC, inclusive) or 'all'.
export type SinceMode = 'unprocessed' | 'date' | 'all'

const DATE = /^\d{4}-\d{2}-\d{2}$/

function field(value: unknown, key: string): unknown {
  return typeof value === 'object' && value !== null && key in value
    ? (value as Record<string, unknown>)[key]
    : undefined
}

export function extractPatient(value: unknown): unknown {
  return field(value, 'patient')
}

export function extractSince(value: unknown): unknown {
  return field(value, 'since')
}

export function sinceMode(since: unknown): SinceMode {
  if (since === undefined) return 'unprocessed'
  return since === 'all' ? 'all' : 'date'
}

/** An empty patient means every patient; `since` '' is "date mode, no date picked yet". */
export function buildFindingsSyncArgs(patient: string, since: string | undefined) {
  const trimmed = patient.trim()
  return {
    ...(trimmed ? { patient: trimmed } : {}),
    ...(since !== undefined ? { since } : {}),
  }
}

export function validateFindingsSyncArgs(value: unknown): string | null {
  if (value !== undefined && (typeof value !== 'object' || value === null)) {
    return 'Args must be an object'
  }
  const patient = extractPatient(value)
  if (patient !== undefined && (typeof patient !== 'string' || patient.trim() === '')) {
    return 'Patient must be a patient id, or left empty for every patient'
  }
  const since = extractSince(value)
  if (since === undefined || since === 'all') return null
  if (typeof since !== 'string' || since === '') return 'Pick the date to start from'
  if (!DATE.test(since) || Number.isNaN(Date.parse(since))) {
    return 'The start date must be a valid YYYY-MM-DD date'
  }
  return null
}
